/** What the current user may do with an estimate — mirrors apps/rest_server/lib/estimate-lifecycle.js. */
import type { Estimate } from '../types';

export const SALES_ROLES = ['full_admin', 'director', 'sales_manager'] as const;
export const APPROVER_ROLES = ['full_admin', 'director'] as const;
/** A deck claim older than this belongs to a run that died (rest_server STALE_CLAIM_MS). */
export const DECK_CLAIM_STALE_MS = 10 * 60 * 1000;

export type EstimateAction =
  | 'save' | 'reprice' | 'delete' | 'request-approval' | 'approve' | 'reject'
  | 'mark-presented' | 'mark-lost' | 'revise' | 'convert' | 'generate-deck';

export interface ActionContext { roles: readonly string[]; userId: string | null }

export const isApprover = (roles: readonly string[]) => roles.some((role) => (APPROVER_ROLES as readonly string[]).includes(role));

export function availableActions(
  estimate: Pick<Estimate, 'status' | 'ownerUserId' | 'result' | 'approvalReasons'>,
  ctx: ActionContext,
): Set<EstimateAction> {
  const approver = isApprover(ctx.roles);
  const actions = new Set<EstimateAction>();
  const blocking = (estimate.result?.warnings ?? []).some((w) => w.blocking);
  const needsApproval = (estimate.approvalReasons ?? []).length > 0;
  switch (estimate.status) {
    case 'draft':
      actions.add('save');
      actions.add('reprice');
      if (approver || (ctx.userId !== null && estimate.ownerUserId === ctx.userId)) actions.add('delete');
      if (!blocking) {
        if (needsApproval) actions.add('request-approval');
        if (!needsApproval || approver) actions.add('approve');
      }
      break;
    case 'pending_approval':
      if (approver) {
        actions.add('approve');
        actions.add('reject');
      }
      break;
    case 'approved':
      for (const action of ['mark-presented', 'revise', 'convert', 'generate-deck'] as const) actions.add(action);
      break;
    case 'presented':
      for (const action of ['mark-lost', 'revise', 'convert', 'generate-deck'] as const) actions.add(action);
      break;
    default:
      break;
  }
  return actions;
}

export type DeckBlock = 'contingency' | 'in_progress' | null;

/** Why Generate deck cannot run right now; the server refuses both cases too. */
export function deckBlock(estimate: Pick<Estimate, 'inputs' | 'deckStatus' | 'deckClaimedAt'>, now = Date.now()): DeckBlock {
  if ((estimate.inputs?.contingencyPct ?? 0) > 0) return 'contingency';
  if (estimate.deckStatus === 'generating' && estimate.deckClaimedAt && now - Date.parse(estimate.deckClaimedAt) < DECK_CLAIM_STALE_MS) {
    return 'in_progress';
  }
  return null;
}
